/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import moment from 'moment';
import { API } from '../configs/api.config';
import {
  ClientInfoData,
  IpInfoIoOutput,
  NumberFormatOptions,
} from '../models/general.model';
import { environment } from 'projects/sharing/environments/environment';
import { RegionIndonesia } from '../models/region-indonesia.model';
import { LS } from '../enums/local-storage.enum';
import { ISOCountries } from '../configs/countries.config';
import { HeaderType } from '../enums/http-headers.enum';
import {
  actionDefault,
  authorizationBearer,
  contentTypeJSON,
} from '../configs/http-headers.config';
import { ActivatedRoute, Router } from '@angular/router';
import { OSInfo } from '@shared/models/os-info.model';
import { BrowserInfo } from '@shared/models/browser-info.model';
import { OsInfoService } from './os-info.service';
import { BrowserInfoService } from './browser-info.service';

@Injectable({
  providedIn: 'root',
})
export class GeneralService implements OnDestroy {
  osInfo!: OSInfo;
  browserInfo!: BrowserInfo;
  userAgent = '';
  browser = '';
  os = '';
  httpClient!: HttpClient;
  osName = '';
  subscriptions: Subscription[] = [];

  constructor(
    private readonly http: HttpClient,
    private osInfoService: OsInfoService,
    private browserInfoService: BrowserInfoService,
  ) {
    this.setDefaultConstructor(http);
  }

  setDefaultConstructor(http: HttpClient): void {
    this.userAgent = window.navigator.userAgent; // User agent string
    this.os = window.navigator.platform; // Operating system
    this.httpClient = http;
    this.osInfo = this.osInfoService.getOSInfo();
    this.browserInfo = this.browserInfoService.getBrowserInfo();
    this.osName = this.osInfo.label;
    this.browser = this.browserInfo.label;
  }

  private setPermissionHeaders(
    headers: HttpHeaders,
    menuId?: string,
    actionId?: string,
  ): HttpHeaders {
    if (menuId && menuId !== '-' && menuId !== actionDefault) {
      headers = headers.set(HeaderType.X_MENU_ID, menuId);
    }
    if (actionId && actionId !== '-' && actionId !== actionDefault) {
      headers = headers.set(HeaderType.X_ACTION_ID, actionId);
    }
    return headers;
  }

  postData(
    data: unknown,
    url: string,
    baseUrl: string,
    menuId?: string,
    actionId?: string,
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    headers = this.setPermissionHeaders(headers, menuId, actionId);
    return this.http.post(baseUrl + url, data, { headers });
  }

  postWithOTPnMFAToken(
    data: unknown,
    url: string,
    otpToken: string,
    mfaToken: string,
    baseUrl: string,
    menuId?: string,
    actionId?: string,
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(HeaderType.X_OTP_TOKEN, `${otpToken}`);
    headers = headers.set(HeaderType.X_MFA_TOKEN, `${mfaToken}`);
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    headers = this.setPermissionHeaders(headers, menuId, actionId);
    return this.http.post(baseUrl + url, data, { headers });
  }

  postWithCustomToken(
    data: unknown,
    url: string,
    token: string,
    baseUrl: string,
    menuId?: string,
    actionId?: string,
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      HeaderType.AUTHORIZATION,
      `${authorizationBearer}${token}`,
    );
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    headers = this.setPermissionHeaders(headers, menuId, actionId);
    return this.http.post(baseUrl + url, data, { headers });
  }

  postWithCaptchaToken(
    data: unknown,
    url: string,
    token: string,
    baseUrl: string,
    menuId?: string,
    actionId?: string,
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(HeaderType.X_CAPTCHA_TOKEN, `${token}`);
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    headers = this.setPermissionHeaders(headers, menuId, actionId);
    return this.http.post(baseUrl + url, data, { headers });
  }

  // Method to send POST request and handle file download
  downloadFile(
    baseUrl: string,
    url: string,
    payload: unknown,
    menuId?: string,
    actionId?: string,
  ): Observable<Blob> {
    let headers = new HttpHeaders();
    headers = this.setPermissionHeaders(headers, menuId, actionId);
    return this.http.post(baseUrl + url, payload, {
      responseType: 'blob',
      headers,
    });
  }

  uploadFile(file: File, menuId?: string, actionId?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    let headers = new HttpHeaders();
    headers = this.setPermissionHeaders(headers, menuId, actionId);
    return this.http.post(API.FILE_BASE_URL + API.FILE_UPLOAD, formData, {
      headers,
    });
  }

  getPublicAPI(url: string): Observable<any> {
    return this.http.get<any>(url);
  }

  getCountryNameByCountryCode(
    http: HttpClient,
    countryCode: string,
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    return http.get(
      environment.API_COUNTRY_INFO_BASE_URL + '?q=' + countryCode,
      { headers },
    );
  }

  getIpInfo(http: HttpClient, token: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    return http.get(environment.API_IP_INFO_BASE_URL + '?token=' + token, {
      headers,
    });
  }

  setClientInfoData(res1: IpInfoIoOutput): void {
    const countryName = ISOCountries.find(
      (c) => c.value === res1.country,
    )?.label;
    const clientInfoData: ClientInfoData = {
      get Timestamp(): string {
        return `${moment().format('DD MMMM YYYY, HH:mm:ss [GMT]Z')}`;
      },
      get LocationStr(): string {
        return `${res1.city}, ${countryName}`;
      },
      get DeviceStr(): string {
        return `${this.Browser}, ${this.OSStr}`;
      },
      IP: res1.ip,
      OSStr: this.osName,
      Browser: this.browser,
      City: res1.city,
      Country: res1.country,
      OS: this.os,
      Useragent: this.userAgent,
    };
    window.localStorage.setItem(LS.CLIENT_INFO, JSON.stringify(clientInfoData));
  }

  clientInfo(): void {
    const token = environment.TOKEN_IPINFO_IO;
    const subscription0 = this.getIpInfo(this.http, token).subscribe({
      next: (res1: IpInfoIoOutput) => {
        this.setClientInfoData(res1);
      },
      error: (err) => console.error(err),
    });
    this.subscriptions.push(subscription0);
  }

  clientInfoNew(): void {
    const token = environment.TOKEN_IPINFO_IO;
    const subscription0 = this.getIpInfo(this.http, token).subscribe({
      next: (res1: IpInfoIoOutput) => {
        this.setClientInfoDataNew(res1);
      },
      error: (err: HttpErrorResponse) => console.error(err),
    });
    this.subscriptions.push(subscription0);
  }

  setClientInfoDataNew(res1: IpInfoIoOutput): void {
    const clientInfoData: ClientInfoData = {
      get Timestamp(): string {
        return `${moment().format('DD MMMM YYYY, HH:mm:ss [GMT]Z')}`;
      },
      get LocationStr(): string {
        return `${res1.city}, ${res1.country_name}`;
      },
      get DeviceStr(): string {
        return `${this.Browser}, ${this.OSStr}`;
      },
      IP: res1.IPv4 as string,
      OSStr: this.osName,
      Browser: this.browser,
      City: res1.city,
      Country: res1.country_name as string,
      OS: this.os,
      Useragent: this.userAgent,
    };
    window.localStorage.setItem(LS.CLIENT_INFO, JSON.stringify(clientInfoData));
  }

  getClientInfo(): ClientInfoData {
    const data: string = window.localStorage.getItem(LS.CLIENT_INFO) as string;
    return this.isJSON(data) ? JSON.parse(data) : {};
  }

  encodeStringToBase64(input: string): string {
    return btoa(input);
  }

  decodeBase64ToString(base64String: string): string {
    return atob(base64String);
  }

  getKeyByValueFromEnum<T extends string | number>(
    enumObj: Record<string, T>,
    value: T,
  ): string | undefined {
    return Object.keys(enumObj).find((key) => enumObj[key] === value);
  }

  setCapitalize1Letter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  setAllCapitalize1Letter(str: string): string {
    const s: string[] = str.split(' ');
    let res = '';
    s.forEach((x) => {
      res += this.setCapitalize1Letter(x) + ' ';
    });
    return res;
  }

  isJSON(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }

  setJSON(str: string | null): Array<string> {
    if (str && this.isJSON(str)) {
      return JSON.parse(str);
    }
    return [];
  }

  getDataRegionIndonesia(): Observable<RegionIndonesia[]> {
    let headers = new HttpHeaders();
    headers = headers.set(HeaderType.CONTENT_TYPE, contentTypeJSON);
    return this.httpClient.get<RegionIndonesia[]>(
      '/assets/json/region-indonesia.json',
      { headers },
    );
  }

  setNumFmt(
    value: number,
    locales: string,
    options: NumberFormatOptions,
  ): string {
    const fmt = new Intl.NumberFormat(locales, options);
    return fmt.format(value);
  }

  removeOneQueryParam(
    activatedRoute: ActivatedRoute,
    router: Router,
    param: string,
  ): void {
    const currentParams = { ...activatedRoute.snapshot.queryParams };
    delete currentParams[param];
    router.navigate([], {
      relativeTo: activatedRoute,
      queryParams: currentParams,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((el) => el.unsubscribe());
  }
}
